import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  IconButton,
  Collapse,
  useTheme
} from '@mui/material';
import { PlayArrow, Stop, Info as InfoIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useActivity } from '../../contexts/ActivityContext';

const PingSweeper = () => {
  const [startIP, setStartIP] = useState('192.168.1.1');
  const [endIP, setEndIP] = useState('192.168.1.10');
  const [isScanning, setIsScanning] = useState(false);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const { updateActivity } = useActivity();
  const theme = useTheme();

  const validateIP = (ip) => {
    const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const handleScan = async () => {
    if (!validateIP(startIP) || !validateIP(endIP)) {
      alert('Please enter valid IP addresses');
      return;
    }

    setIsScanning(true);
    setResults([]);
    setProgress(0);
    
    // Simulate ping sweep (in a real app, this would be an API call)
    const startOctets = startIP.split('.').map(Number);
    const endOctets = endIP.split('.').map(Number);
    const totalIPs = (endOctets[3] - startOctets[3]) + 1;
    
    for (let i = 0; i < totalIPs; i++) {
      if (!isScanning) break;
      
      const currentIP = `${startOctets[0]}.${startOctets[1]}.${startOctets[2]}.${startOctets[3] + i}`;
      const isAlive = Math.random() > 0.7; // 30% chance of being alive for demo
      
      setTimeout(() => {
        setResults(prev => [...prev, {
          ip: currentIP,
          status: isAlive ? 'alive' : 'dead',
          timestamp: new Date().toISOString(),
          responseTime: isAlive ? Math.floor(Math.random() * 50) + 10 : null
        }]);
        
        setProgress(((i + 1) / totalIPs) * 100);
        
        if (i === totalIPs - 1) {
          setIsScanning(false);
          updateActivity('Ping Sweep', `Completed scan from ${startIP} to ${endIP}`, { 
            totalIPs,
            alive: results.filter(r => r.status === 'alive').length + (isAlive ? 1 : 0)
          });
        }
      }, i * 100); // 100ms delay between pings
    }
  };

  const handleStopScan = () => {
    setIsScanning(false);
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Box>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Ping Sweep
            <Tooltip title="Scan a range of IP addresses to find active hosts">
              <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
            </Tooltip>
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Start IP"
                variant="outlined"
                value={startIP}
                onChange={(e) => setStartIP(e.target.value)}
                disabled={isScanning}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="End IP"
                variant="outlined"
                value={endIP}
                onChange={(e) => setEndIP(e.target.value)}
                disabled={isScanning}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              {!isScanning ? (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleScan}
                  startIcon={<PlayArrow />}
                  disabled={isScanning}
                >
                  Start
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  onClick={handleStopScan}
                  startIcon={<Stop />}
                >
                  Stop
                </Button>
              )}
            </Grid>
          </Grid>

          {isScanning && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="caption" color="textSecondary">
                Scanning... {Math.round(progress)}% complete
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Box 
              onClick={handleExpandClick}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                color: 'primary.main',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <Typography variant="subtitle2">
                {results.length > 0 ? `Results (${results.length} IPs)` : 'No results yet'}
              </Typography>
              <ExpandMoreIcon 
                sx={{ 
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: theme.transitions.create('transform', {
                    duration: theme.transitions.duration.shortest,
                  }),
                  ml: 1
                }}
              />
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 300, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>IP Address</TableCell>
                      <TableCell align="right">Status</TableCell>
                      <TableCell align="right">Response Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((result, index) => (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">
                          {result.ip}
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={result.status === 'alive' ? 'Alive' : 'No Response'}
                            color={result.status === 'alive' ? 'success' : 'default'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {result.responseTime ? `${result.responseTime}ms` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {results.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>
                          No scan results yet. Enter IP range and click Start to begin.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PingSweeper;
